<template>
    <div>
        <h1 class="page-title">{{ $t("catalogue.blacklisting.title") }}</h1>
        <div class="flex w-full justify-end mb-3">
            
            

            <Button
                label="Bloquer un utilisateur"
                @click="$router.push({ name: 'enabledUsersList' })"
                icon="pi pi-ban"
                aria-label="Bloquer un utilisateur"
                class="button-add"
            />
        </div>


       
        
        <DataTable
            lazy
            paginator
            removableSort
            currentPageReportTemplate="Affichage de {first} à {last} sur {totalRecords} entrées"
            template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            :value="tableData"
            :rows="rows"
            :totalRecords="totalRecords"
            :rowsPerPageOptions="rowsPerPageOptions"
            :currentPage="currentPage"
            :rowHover="true"
            :loading="isLoading"
            @sort="onSort($event)"
            @page="onPaginate"
        >
            <template #header>
                <div class="flex justify-between items-center my-1">
                    <span class="p-input-icon-left mr-3">
                        <i class="pi pi-search" />
                        <InputText
                            type="text"
                            class="form-control w-64 pl-10 pr-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            @keyup.enter="onFilterDt"
                            placeholder="Rechercher..."
                            v-model="criteria"
                            @input="onClearInputFilter"
                            />
                    </span>
                   
                    
                </div>
                <div class="flex justify-end ml-auto mt-10">Utilisateurs blacklistés : {{ totalRecords }} </div>
            </template>


            <div>
                <Column headerStyle="width: 3rem" />
                <Column field="user.firstname" header="Nom" sortable>
                    <template #body="slotProps">
                        <span>{{ slotProps.data.name }}</span>
                    </template>
                </Column>
                <Column field="user.company.name" header="Société" sortable>
                    <template #body="slotProps">
                        <span>{{ slotProps.data.company }}</span>
                    </template>
                </Column>
                <Column field="user.email" header="Email" sortable>
                    <template #body="slotProps">
                        <span>{{ slotProps.data.email }}</span>
                    </template>
                </Column>
                <Column field="listedAt" header="Date du dernier blocage" sortable>
                    <template #body="slotProps">
                        <span>{{ slotProps.data.blockDate }}</span>
                    </template>
                </Column>
                <Column field="user.roles" header="Rôle" sortable>
                    <template #body="slotProps">
                        <Tag v-if="slotProps.data.role.length > 0"
                             :severity="getRoleTag(slotProps.data.role).severity"
                             :value="getRoleTag(slotProps.data.role).value"
                             rounded
                        ></Tag>
                    </template>
                </Column>
                <Column field="active" header="Etat" sortable>
                    <template #body="slotProps">
                        <i :class="slotProps.data.active ? 'pi pi-times' : 'pi pi-check'"
                           :style="{ color: slotProps.data.active ? 'red' : 'green' }"
                        ></i>
                    </template>
                </Column>
                <Column field="state" header="Statut" sortable>
                    <template #body="slotProps">
                        <Tag :severity="slotProps.data.active ? 'warning' : 'success'"
                             :value="slotProps.data.active ? 'Non traité' : 'Traité'"
                             rounded></Tag>
                    </template>
                </Column>
                <Column field="action" header="Action">
                    <template #body="slotProps">
                        <Button icon="pi pi-search" size="small" unstyled="unstyled" @click="redirectToDetails(slotProps.data.uuid)" />
                    </template>
                </Column>
            </div>
        </DataTable>
    </div>
</template>

<script lang="ts" setup>
import {onMounted, ref} from "vue";
import {useRouter} from "vue-router";
import {UserBlackList} from "@/models/blacklisting/UserBlackList";
import {getUsersBlackListed} from "@/_services/blackList/blacklist";
import Button from "primevue/button";

const router = useRouter();
const isLoading = ref<boolean>(true);
const usersBlackList = Array<UserBlackList>();
const tableData = ref<Array<UserBlackList>>(usersBlackList);
const rows = ref<number>(10);
const totalRecords = ref<number>(0);
const page = ref<number>(1);
const defaultRowsPerPage = 10;
const rowsPerPageOptions = [defaultRowsPerPage, 25, 100];
const currentPage = ref<number>(0);
const criteria = ref<string>("");
const modeSortable = ref<boolean>(false);
const defaultsSort = {
    fieldLabel: "order[listedAt]",
    fieldValue: "desc",
};

onMounted(async () => {
    await populateDatatable(rows.value, page.value, criteria.value, defaultsSort);
});

const populateDatatable = async (
    itemsPerPage: number,
    page: number,
    criteria?:string,
    sort?: { fieldLabel: string; fieldValue: string; }
) => {
    const [isLoadingDt, totalRecordsDt, tableDataDt] = await getUsersBlackListed(itemsPerPage, page, criteria, sort);
    isLoading.value = isLoadingDt;
    totalRecords.value = totalRecordsDt;
    tableData.value = tableDataDt;
};

const onPaginate = async (e) => {
    rows.value = e.rows;
    populateDatatable(e.rows, e.page + 1, criteria.value, defaultsSort);
};

const onFilterDt = () => {
    if (criteria.value != "") {
        populateDatatable(rows.value, page.value, criteria.value);
    }
};

const onClearInputFilter = () => {
    if (criteria.value === "") {
        populateDatatable(rows.value, page.value);
    }
};

const onSort = (e) => {
    if (e.sortField) {
        const sort = {
            fieldLabel: "order[" + e.sortField + "]",
            fieldValue: e.sortOrder === 1 ? "asc" : "desc",
        };
        modeSortable.value = true;
        populateDatatable(rows.value, page.value, criteria.value, sort);
    } else {
        modeSortable.value = false;
        populateDatatable(rows.value, page.value, criteria.value, defaultsSort);
    }
};

const getRoleTag = (roles: string[]) => {
    const roleMappings = {
        'ROLE_ADMIN': { severity: 'warning', value: 'Admin' },
        'ROLE_MANAGER': { severity: 'danger', value: 'Manager' },
        'ROLE_CUSTOMER': { severity: 'info', value: 'Utilisateur' },
        'ROLE_SUPPLIER': { severity: 'contrast', value: 'Fournisseur' },
        'ROLE_DEMO': { severity: 'success', value: 'Demo' }
    };
    const rolesPriority = ['ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_CUSTOMER', 'ROLE_SUPPLIER', 'ROLE_DEMO'];
    for (const priorityRole of rolesPriority) {
        if (roles.includes(priorityRole)) {
            return roleMappings[priorityRole];
        }
    }

    return { severity: 'Primary', value: 'Inconnu' };
};

const redirectToDetails = (uuid: string) => {
    router.push({name: "UserBlacklistHistorique", params: { uuid: uuid }});
};
</script>